/**
 * Return the YouTube video ID (11-char) from a URL string, or null if none found.
 * Handles:
 *  - https://www.youtube.com/watch?v=VIDEOID
 *  - https://youtu.be/VIDEOID
 *  - https://www.youtube.com/embed/VIDEOID
 *  - https://www.youtube.com/v/VIDEOID
 *  - https://www.youtube.com/shorts/VIDEOID
 *  - query strings with other params (t, list, etc.)
 */
export const getYouTubeIdFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  // quick invalid catch
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Helper to validate a candidate id (YouTube ids are 11 chars of letters, digits, - or _)
  const isValidId = (id) => typeof id === 'string' && /^[A-Za-z0-9_-]{11}$/.test(id);

  try {
    const u = new URL(trimmed);

    // host normalize (strip subdomain)
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();

    // youtu.be short link
    if (host === 'youtu.be') {
      const candidate = u.pathname.slice(1).split(/[/?#]/)[0]; // first path segment
      if (isValidId(candidate)) return candidate;
    }

    // youtube domains
    if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
      // 1) typical watch?v=VIDEOID
      if (u.searchParams.has('v')) {
        const candidate = u.searchParams.get('v');
        if (isValidId(candidate)) return candidate;
      }

      // 2) embed, v, shorts URLs: /embed/ID, /v/ID, /shorts/ID
      const pathParts = u.pathname.split('/').filter(Boolean); // remove empty segments
      // find possible ID after known segments
      const idx = pathParts.findIndex(p => ['embed', 'v', 'shorts'].includes(p));
      if (idx !== -1 && pathParts.length > idx + 1) {
        const candidate = pathParts[idx + 1].split(/[/?#]/)[0];
        if (isValidId(candidate)) return candidate;
      }

      // 3) sometimes URLs like /watch; fallback: maybe the last path segment is the id (rare)
      const last = pathParts[pathParts.length - 1];
      if (isValidId(last)) return last;
    }
  } catch (e) {
    // If URL constructor fails (maybe string isn't a full URL), fall back to regex
  }

  // Final fallback: regex search anywhere in the string for common patterns
  // Matches v=ID or /embed/ID or youtu.be/ID or /v/ID or /shorts/ID
  const regex = /(?:v=|\/(?:embed|v|shorts)\/|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const m = trimmed.match(regex);
  if (m && m[1] && isValidId(m[1])) return m[1];

  return null;
}

export const getYouTubeThumbnail = (videoId, quality = "hq") => {
  if (!videoId || typeof videoId !== "string") return null;

  const qualities = {
    default: "default.jpg",
    mq: "mqdefault.jpg",
    hq: "hqdefault.jpg",
    sd: "sddefault.jpg",
    maxres: "maxresdefault.jpg"
  };

  const file = qualities[quality] || qualities.hq;

  return `https://img.youtube.com/vi/${videoId}/${file}`;
}